import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Card } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { DatePicker } from '../../../components/ui/date-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../components/ui/command'
import { router } from '@inertiajs/react'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface User {
  id: number
  email: string
  full_name: string
}

interface Audit {
  id: number
  user: User | null
  action: string
  auditable_type: string
  auditable_id: number
  audited_changes: Record<string, any>
  version: number
  comment: string | null
  remote_address: string | null
  created_at: string
}

interface Pagination {
  page: number
  pages: number
  count: number
  from: number
  to: number
}

interface Filters {
  users: User[]
  actions: string[]
  auditable_types: string[]
  selected_user_id?: string
  selected_action?: string
  selected_auditable_type?: string
  start_date?: string
  end_date?: string
  selected_user_name?: string
}

interface AuditsIndexProps {
  audits: Audit[]
  pagination: Pagination
  filters: Filters
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return 'null'
  if (typeof val === 'boolean') return val ? 'true' : 'false'
  if (typeof val === 'string' || typeof val === 'number') return String(val)
  return JSON.stringify(val, null, 2)
}

function ChangesCell({ changes, t }: { changes: Record<string, any>; t: (key: string) => string }) {
  const [open, setOpen] = useState(false)

  if (!changes || Object.keys(changes).length === 0) {
    return <span className="text-sm text-muted-foreground">{t('audits.noChanges')}</span>
  }

  const entries = Object.entries(changes)
  const summary = entries
    .slice(0, 2)
    .map(([key, value]) => {
      if (Array.isArray(value) && value.length === 2) {
        return `${key}: ${formatValue(value[0]).substring(0, 20)} → ${formatValue(value[1]).substring(0, 20)}`
      }
      return `${key} ${t('audits.setTo')} ${formatValue(value).substring(0, 20)}`
    })
    .join(', ')

  const remaining = entries.length > 2 ? ` +${entries.length - 2}` : ''

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-sm text-foreground max-w-md truncate text-left hover:text-primary cursor-pointer">
          {summary}{remaining}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-h-80 overflow-y-auto p-3" align="start">
        <div className="text-xs font-medium text-muted-foreground mb-2">{t('audits.allChanges')}</div>
        <div className="space-y-2">
          {entries.map(([key, value]) => (
            <div key={key} className="border-b border-border pb-2 last:border-0 last:pb-0">
              <div className="text-xs font-semibold text-foreground mb-1">{key}</div>
              {Array.isArray(value) && value.length === 2 ? (
                <div className="space-y-0.5">
                  <div className="text-xs bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 rounded px-1.5 py-0.5 font-mono break-all">
                    - {formatValue(value[0])}
                  </div>
                  <div className="text-xs bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded px-1.5 py-0.5 font-mono break-all">
                    + {formatValue(value[1])}
                  </div>
                </div>
              ) : (
                <div className="text-xs bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded px-1.5 py-0.5 font-mono break-all">
                  {formatValue(value)}
                </div>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function Index({ audits, pagination, filters }: AuditsIndexProps) {
  const { t } = useTranslation('admin')
  const [userId, setUserId] = useState(filters.selected_user_id || 'all')
  const [userLabel, setUserLabel] = useState(filters.selected_user_name || '')
  const [action, setAction] = useState(filters.selected_action || 'all')
  const [auditableType, setAuditableType] = useState(filters.selected_auditable_type || 'all')
  const [startDate, setStartDate] = useState(filters.start_date || '')
  const [endDate, setEndDate] = useState(filters.end_date || '')

  // User search state
  const [userSearchOpen, setUserSearchOpen] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<User[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const searchUsers = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 2) {
      setUserSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/admin/users/search?q=${encodeURIComponent(query)}`)
        if (res.ok) {
          const data = await res.json()
          setUserSearchResults(data)
        }
      } catch {
        // ignore
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleUserSearchInput = (value: string) => {
    setUserSearchQuery(value)
    searchUsers(value)
  }

  const handleSelectUser = (user: User) => {
    setUserId(user.id.toString())
    setUserLabel(user.full_name || user.email)
    setUserSearchOpen(false)
    setUserSearchQuery('')
  }

  const handleClearUser = () => {
    setUserId('all')
    setUserLabel('')
    setUserSearchQuery('')
    setUserSearchResults([])
  }

  const handleSearch = () => {
    const params: Record<string, string> = {}
    if (userId && userId !== 'all') params.user_id = userId
    if (action && action !== 'all') params.audit_action = action
    if (auditableType && auditableType !== 'all') params.auditable_type = auditableType
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate

    router.get('/admin/audits', params, { preserveState: true })
  }

  const handleReset = () => {
    handleClearUser()
    setAction('all')
    setAuditableType('all')
    setStartDate('')
    setEndDate('')
    router.get('/admin/audits', {}, { preserveState: true })
  }

  const getActionBadgeVariant = (action: string) => {
    switch (action) {
      case 'create':
        return 'default'
      case 'update':
        return 'secondary'
      case 'destroy':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">{t('audits.title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('audits.description')} ({t('audits.total', { count: pagination.count })})
                </p>
              </div>

              {/* Filters */}
              <Card className="shadow-none mb-6 p-3">
                <div className="flex items-end justify-between flex-wrap gap-3">
                  <div className="flex items-end flex-wrap gap-3 flex-1">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-foreground block mb-1">
                        {t('audits.filters.user')}
                      </label>
                      <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={userSearchOpen}
                            className="h-9 w-full justify-between text-sm font-normal"
                          >
                            {userId !== 'all' ? userLabel : t('audits.filters.allUsers')}
                            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder={t('audits.filters.searchUser')}
                              value={userSearchQuery}
                              onValueChange={handleUserSearchInput}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {isSearching
                                  ? '...'
                                  : userSearchQuery.length < 2
                                    ? t('audits.filters.searchUser')
                                    : t('audits.filters.noUserFound')}
                              </CommandEmpty>
                              <CommandGroup>
                                {userId !== 'all' && (
                                  <CommandItem onSelect={handleClearUser}>
                                    <span className="text-muted-foreground">{t('audits.filters.allUsers')}</span>
                                  </CommandItem>
                                )}
                                {userSearchResults.map((user) => (
                                  <CommandItem
                                    key={user.id}
                                    onSelect={() => handleSelectUser(user)}
                                  >
                                    <CheckIcon className={cn("mr-2 h-4 w-4", userId === user.id.toString() ? "opacity-100" : "opacity-0")} />
                                    <div>
                                      <div className="text-sm">{user.full_name}</div>
                                      <div className="text-xs text-muted-foreground">{user.email}</div>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-foreground block mb-1">
                        {t('audits.filters.action')}
                      </label>
                      <Select value={action} onValueChange={setAction}>
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder={t('audits.filters.allActions')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('audits.filters.allActions')}</SelectItem>
                          {filters.actions.map((act) => (
                            <SelectItem key={act} value={act}>
                              {act}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-foreground block mb-1">
                        {t('audits.filters.modelType')}
                      </label>
                      <Select value={auditableType} onValueChange={setAuditableType}>
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder={t('audits.filters.allTypes')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('audits.filters.allTypes')}</SelectItem>
                          {filters.auditable_types.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1 flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-foreground block mb-1">
                          {t('audits.filters.startDate')}
                        </label>
                        <DatePicker
                          value={startDate}
                          onChange={setStartDate}
                          placeholder={t('audits.filters.startDate')}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-medium text-foreground block mb-1">
                          {t('audits.filters.endDate')}
                        </label>
                        <DatePicker
                          value={endDate}
                          onChange={setEndDate}
                          placeholder={t('audits.filters.endDate')}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Button onClick={handleSearch} className="h-9 px-4 text-sm">
                      {t('audits.buttons.search')}
                    </Button>
                    <Button onClick={handleReset} variant="outline" className="h-9 px-4 text-sm">
                      {t('audits.buttons.reset')}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Audit Logs Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('audits.table.timestamp')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('audits.table.user')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('audits.table.action')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('audits.table.model')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('audits.table.changes')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('audits.table.ip')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {audits.length > 0 ? (
                        audits.map((audit) => (
                          <tr key={audit.id} className="hover:bg-muted transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-foreground">
                                {new Date(audit.created_at).toLocaleString(getDateLocale(), {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {audit.user ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 flex-shrink-0 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold text-xs">
                                    {audit.user.email.substring(0, 2).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-foreground">
                                      {audit.user.full_name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {audit.user.email}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">{t('audits.system')}</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge variant={getActionBadgeVariant(audit.action)}>
                                {audit.action}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-foreground">{audit.auditable_type}</div>
                              <div className="text-xs text-muted-foreground">{t('audits.table.id')} {audit.auditable_id}</div>
                            </td>
                            <td className="px-6 py-4">
                              <ChangesCell changes={audit.audited_changes} t={t} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-muted-foreground">
                                {audit.remote_address || '-'}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                            {t('audits.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-border bg-card">
                    <div className="text-sm text-foreground">
                      {t('audits.pagination.showing', { from: pagination.from, to: pagination.to, count: pagination.count })}
                    </div>
                    <div className="flex gap-2">
                      {pagination.page > 1 && (
                        <Button
                          variant="outline"
                          onClick={() => router.get(`/admin/audits?page=${pagination.page - 1}`, {}, { preserveState: true })}
                        >
                          {t('audits.pagination.previous')}
                        </Button>
                      )}
                      {pagination.page < pagination.pages && (
                        <Button
                          variant="outline"
                          onClick={() => router.get(`/admin/audits?page=${pagination.page + 1}`, {}, { preserveState: true })}
                        >
                          {t('audits.pagination.next')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
